import Alpine from "alpinejs";
import { registerConsentStore } from "./ConsentStore.js";
import { registerHeaderOverlay } from "./HeaderOverlay.js";
import { registerPrintHelpers } from "./Print.js";
window.Alpine = Alpine;

// Global cookie consent store, backed by CookieFirst - defined immediately so it's
// available when Alpine starts. See ConsentStore.js for the CookieFirst contract.
registerConsentStore(Alpine);

// Transparent header over selected content elements - see HeaderOverlay.js
registerHeaderOverlay();

// Open collapsed accordions / FAQs while printing - see Print.js
registerPrintHelpers();

document.addEventListener('alpine:init', () => {
    // Lazy video loading - loads video when it enters viewport
    Alpine.data('lazyVideo', () => ({
        _observer: null,
        observe() {
            if (!('IntersectionObserver' in window)) {
                // Fallback: load immediately if IntersectionObserver not supported
                this.load();
                return;
            }
            this._observer = new IntersectionObserver((entries, obs) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.load();
                        obs.unobserve(entry.target);
                    }
                });
            }, {
                rootMargin: '200px 0px' // Start loading 200px before entering viewport
            });
            this._observer.observe(this.$el);
        },
        load() {
            const video = this.$el;
            video.preload = 'metadata';
            video.load();
        },
        destroy() {
            if (this._observer) {
                this._observer.disconnect();
            }
        }
    }));

    // Play on hover (desktop) / play in viewport (mobile)
    Alpine.data('hoverVideo', () => ({
        _observer: null,
        _isMobile: false,
        _isLoaded: false,
        init() {
            // Detect mobile/touch device
            this._isMobile = window.matchMedia('(max-width: 1024px)').matches ||
                             'ontouchstart' in window ||
                             navigator.maxTouchPoints > 0;

            const video = this.$el;
            video.preload = 'metadata';

            if (this._isMobile) {
                this._setupMobileObserver();
            }
        },
        _setupMobileObserver() {
            if (!('IntersectionObserver' in window)) {
                return;
            }
            this._observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this._loadAndPlay();
                    } else {
                        this.pause();
                    }
                });
            }, {
                threshold: 0.5 // Play when 50% visible
            });
            this._observer.observe(this.$el);
        },
        _loadAndPlay() {
            const video = this.$el;
            if (!this._isLoaded) {
                video.load();
                this._isLoaded = true;
            }
            video.play().catch(() => {
                // Autoplay blocked - silently fail
            });
        },
        play() {
            if (this._isMobile) return; // Mobile uses observer
            this._loadAndPlay();
        },
        pause() {
            const video = this.$el;
            if (!video.paused) {
                video.pause();
            }
        },
        destroy() {
            if (this._observer) {
                this._observer.disconnect();
            }
        }
    }));

    Alpine.data('page', () => ({
        // state
        isOpenMobileMenu: false,
        overflowHidden: false,
        openMenuPages: [],
        consents: [],

        // ==== INIT: lazy background loader ====
        init() {
            const root = this.$root || document;
            const lazyBackgroundImages = Array.from(root.querySelectorAll('.lazyloading-bg'));

            if ('IntersectionObserver' in window) {
                const observer = new IntersectionObserver((entries, obs) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            // once visible, drop the blocking class so the bg-image can apply
                            entry.target.classList.remove('lazyloading-bg');
                            obs.unobserve(entry.target);
                        }
                    });
                }, {
                    // start a bit early so images are ready when you scroll to them
                    rootMargin: '200px 0px'
                });

                lazyBackgroundImages.forEach((el) => observer.observe(el));
                // keep a ref if you ever want to disconnect manually
                this._lazyBgObserver = observer;
            } else {
                // Fallback: no IO support → just load immediately
                lazyBackgroundImages.forEach((el) => el.classList.remove('lazyloading-bg'));
            }

            this.initRteFoldouts();
            this.scrollToInitialAnchor();
        },
        scrollToInitialAnchor() {
            const hash = window.location.hash;
            if (!hash || hash.length < 2) {
                return;
            }

            const raw = hash.slice(1);
            const decoded = decodeURIComponent(raw);
            const cssEscape = (value) => {
                if (window.CSS && typeof window.CSS.escape === 'function') {
                    return window.CSS.escape(value);
                }
                return value.replace(/["\\]/g, '\\$&');
            };

            const target =
                document.getElementById(decoded) ||
                document.querySelector(`[name="${cssEscape(decoded)}"]`);

            if (!target) {
                return;
            }

            const prefersReduced = window.matchMedia &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            const performScroll = () => {
                try {
                    target.scrollIntoView({
                        behavior: prefersReduced ? 'auto' : 'smooth',
                        block: 'start'
                    });
                } catch (err) {
                    target.scrollIntoView();
                }
            };

            requestAnimationFrame(() => {
                setTimeout(performScroll, 0);
            });
        },
        initRteFoldouts() {
            const root = this.$root || document.body;
            if (!root) {
                return;
            }

            const openLabel = root.dataset.rteFoldoutOpen || 'Show more';
            const closeLabel = root.dataset.rteFoldoutClose || 'Show less';

            const markers = Array.from(root.querySelectorAll('.fold-break, .page-break'));
            markers.forEach((marker) => {
                const parent = marker.parentNode;
                if (!parent || parent.nodeType !== 1) {
                    return;
                }

                const foldout = document.createElement('div');
                foldout.className = 'rte-foldout';
                foldout.dataset.foldOpenLabel = marker.dataset.foldOpen || '';
                foldout.dataset.foldCloseLabel = marker.dataset.foldClose || '';

                let sibling = marker.nextSibling;
                while (sibling) {
                    const nextSibling = sibling.nextSibling;
                    foldout.appendChild(sibling);
                    sibling = nextSibling;
                }

                const foldoutContainer = document.createElement('div');
                foldoutContainer.className = 'rte-foldout-container';
                const foldoutInner = document.createElement('div');
                foldoutInner.className = 'rte-foldout-inner';
                while (foldout.firstChild) {
                    foldoutInner.appendChild(foldout.firstChild);
                }
                foldoutContainer.appendChild(foldoutInner);
                foldout.appendChild(foldoutContainer);

                parent.insertBefore(foldout, marker.nextSibling);
                marker.remove();
            });

            root.querySelectorAll('.rte-foldout').forEach((foldout) => {
                if (foldout.dataset.rteFoldoutInitialized) {
                    return;
                }

                const buttonWrap = document.createElement('div');
                buttonWrap.className = 'rte-foldout-toggle-wrap';
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'button-ghost-tiny color-1';
                buttonWrap.appendChild(button);

                const bottomButtonWrap = document.createElement('div');
                bottomButtonWrap.className = 'rte-foldout-toggle-wrap rte-foldout-toggle-wrap--bottom';
                const bottomButton = document.createElement('button');
                bottomButton.type = 'button';
                bottomButton.className = 'button-ghost-tiny color-1';
                bottomButtonWrap.appendChild(bottomButton);

                const foldoutOpenLabel = foldout.dataset.foldOpenLabel || openLabel;
                const foldoutCloseLabel = foldout.dataset.foldCloseLabel || closeLabel;

                const setState = (isOpen) => {
                    foldout.classList.toggle('is-open', isOpen);
                    button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
                    bottomButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
                    button.textContent = isOpen ? foldoutCloseLabel : foldoutOpenLabel;
                    bottomButton.textContent = foldoutCloseLabel;
                    bottomButtonWrap.hidden = !isOpen;
                    buttonWrap.hidden = isOpen;
                };

                setState(false);
                const toggle = () => setState(!foldout.classList.contains('is-open'));
                button.addEventListener('click', toggle);
                bottomButton.addEventListener('click', toggle);

                foldout.dataset.rteFoldoutInitialized = '1';
                foldout.parentNode.insertBefore(buttonWrap, foldout);
                foldout.parentNode.insertBefore(bottomButtonWrap, foldout.nextSibling);
            });
        },
        hasConsent(requirements = []) {
            return Alpine.store('consent').has(requirements);
        },
        allowConsents(consents) {
            Alpine.store('consent').allow(consents);
        },
        // ==== mobile menu ====
        toggleMobileMenu() {
            if (this.isOpenMobileMenu) {
                this.closeMobileMenu();
            } else {
                this.openMobileMenu();
            }
        },
        closeMobileMenu() {
            this.isOpenMobileMenu = false;
            this.overflowHidden = false;
            this.closeAllMenues();
        },
        openMobileMenu() {
            this.isOpenMobileMenu = true;
            this.overflowHidden = true;
        },

        // ==== subpages ====
        toggleMenuSubpage(id) {
            if (this.isMenuOpen(id)) {
                this.closeMenuSubpage(id);
            } else {
                this.openMenuSubpage(id);
            }
        },
        openMenuSubpage(id) {
            this.openMenuPages.push(id);
        },
        closeMenuSubpage(id) {
            this.openMenuPages = this.openMenuPages.filter((item) => item !== id);
        },
        closeAllMenues() {
            this.openMenuPages = [];
        },
        isMenuOpen(id) {
            return this.openMenuPages.includes(id);
        },
    }));

    Alpine.data('desktopMenuItem', () => ({
        open: false,
        toggleMenu() { this.open = !this.open },
        closeMenu()  { this.open = false },
        openMenu()   { this.open = true }
    }));

    // Mega menu (hd_submenu_style = 4). Which second level entry the panel on the right belongs
    // to. It starts at 0 rather than -1 so the panel is filled the moment the submenu opens,
    // which is what the design shows - there is no "nothing selected" state.
    //
    // A component rather than an inline x-data object because Fluid reads a curly brace in an
    // attribute as its own array syntax, so { current: 0 } cannot be written in the template.
    Alpine.data('megaMenu', () => ({
        current: 0,
        select(index) { this.current = index },
        isCurrent(index) { return this.current === index },

        // The arrow button: open that entry's panel and put the focus inside it. Without this the
        // third level is keyboard-unreachable in practice - the panels are rendered after the
        // whole list, so Tab from an entry goes to the next entry, not into what it just opened.
        // The panel itself takes the focus (tabindex="-1" plus role/aria-label on it), so a
        // screen reader announces which group was entered before the links in it.
        enter(index) {
            this.select(index);
            // this.root, not $el: inside an x-on expression Alpine points $el at the element the
            // directive sits on - the arrow button - so a lookup from $el would search the button
            // and quietly find nothing. $nextTick is enough for the rest: x-show has already set
            // the panel's display by then, and focus() on a display:none box does nothing.
            this.$nextTick(() => {
                const panel = this.root.querySelector(`.hd-megamenu-panel[data-index="${index}"]`);
                if (panel) {
                    panel.focus();
                }
            });
        },

        init() {
            // Held as a plain property rather than read through a magic on every call: $el points
            // at whatever element the expression sits on, and $root is only resolved reliably
            // while Alpine is evaluating a directive - neither survives a deferred callback.
            this.root = this.$el;
            this.align = this.align.bind(this);
            window.addEventListener('resize', this.align, { passive: true });

            // "open" belongs to the desktopMenuItem around this one; Alpine resolves it up the
            // scope chain. Measuring has to wait for it, because a display:none element has no
            // offsetParent - which is exactly what an aligning pass needs.
            this.$watch('open', (isOpen) => {
                if (isOpen) {
                    this.$nextTick(this.align);
                }
            });
        },

        destroy() {
            window.removeEventListener('resize', this.align);
        },

        // The panel's surface runs edge to edge, but it is rendered inside a navigation item, so
        // what it is absolutely positioned against is not the viewport: it is the list item in
        // header2 and the navigation row in header1 and header3. Both numbers have to be
        // measured - CSS can express neither "start at the left edge of the window" nor "be as
        // wide as the window" from in here without a transform, and a transform on this element
        // would fight the one the open/close animation already puts on it.
        //
        // clientWidth, not innerWidth: the scrollbar is not part of the layout, and a panel as
        // wide as innerWidth would add a horizontal scrollbar of its own.
        align() {
            const wrap = this.root.parentElement;
            const anchor = wrap.offsetParent;
            if (!anchor) {
                return;
            }

            const viewport = document.documentElement.clientWidth;
            wrap.style.right = 'auto';
            wrap.style.left = `${Math.round(-anchor.getBoundingClientRect().left)}px`;
            wrap.style.width = `${viewport}px`;
        }
    }));

    // Header2 component - two-tier header with scroll effects
    Alpine.data('header2', () => ({
        scrolled: false,
        topBarVisible: true,
        mobileMenuOpen: false,
        openMenuPages: [],

        // The search panel. The state lives up here rather than next to the toggle because the
        // two halves are far apart in the markup: the toggle sits in the meta bar or in the
        // navigation row, the panel is a direct child of the header so it can span the content
        // container. The header is the only scope both are inside.
        searchOpen: false,

        init() {
            this.handleScroll();
            window.addEventListener('scroll', () => this.handleScroll(), { passive: true });
        },

        handleScroll() {
            const scrollY = window.scrollY;
            this.scrolled = scrollY > 50;
            this.topBarVisible = scrollY < 100;
        },

        toggleSearch() {
            this.searchOpen = !this.searchOpen;
            if (!this.searchOpen) {
                return;
            }
            // The field is the only reason to open the panel, so the caret belongs in it.
            this.$nextTick(() => {
                if (this.$refs.headerSearchInput) {
                    this.$refs.headerSearchInput.focus();
                }
            });
        },

        closeSearch() {
            this.searchOpen = false;
        },

        toggleMobileMenu() {
            this.mobileMenuOpen = !this.mobileMenuOpen;
            document.body.style.overflow = this.mobileMenuOpen ? 'hidden' : '';
        },

        closeMobileMenu() {
            this.mobileMenuOpen = false;
            document.body.style.overflow = '';
            this.closeAllMenues();
        },

        toggleMenuSubpage(id) {
            if (this.isMenuOpen(id)) {
                this.closeMenuSubpage(id);
            } else {
                this.openMenuSubpage(id);
            }
        },

        openMenuSubpage(id) {
            this.openMenuPages.push(id);
        },

        closeMenuSubpage(id) {
            this.openMenuPages = this.openMenuPages.filter((item) => item !== id);
        },

        closeAllMenues() {
            this.openMenuPages = [];
        },

        isMenuOpen(id) {
            return this.openMenuPages.includes(id);
        }
    }));

    // Newsletter subscription component
    Alpine.data('newsletter', () => ({
        email: '',
        status: 'idle', // idle, loading, success, error
        errorMessage: '',

        async subscribe() {
            if (!this.email || this.status === 'loading') return;

            this.status = 'loading';
            this.errorMessage = '';

            try {
                const form = this.$el.querySelector('form');
                const action = form.getAttribute(':action')?.replace(/'/g, '') || form.action;

                if (!action) {
                    // If no action defined, just show success for demo
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    this.status = 'success';
                    return;
                }

                const response = await fetch(action, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams({ email: this.email })
                });

                if (response.ok) {
                    this.status = 'success';
                    this.email = '';
                } else {
                    throw new Error('Subscription failed');
                }
            } catch (error) {
                this.status = 'error';
                this.errorMessage = error.message || 'An error occurred. Please try again.';
                setTimeout(() => {
                    this.status = 'idle';
                }, 3000);
            }
        }
    }));

    // The announcement bar above the header. The dismissal is remembered per campaign:
    // the stored value is the bar's data-version, so bumping the version in the site
    // settings brings the bar back for everyone who had closed the previous one.
    //
    // Storage is wrapped because a private window, blocked site data or a preview can make
    // localStorage throw on access rather than return null - the bar must still render.
    Alpine.data('announcementBanner', () => ({
        closed: false,
        storageKey: 'hd-banner',

        init() {
            // $el is only the component root here. Inside x-on it is the element the
            // handler sits on - the close button - which carries no data-version, so the
            // key has to be read once and kept.
            this.storageKey = 'hd-banner-' + (this.$el.dataset.version || '1');
            try {
                this.closed = window.localStorage.getItem(this.storageKey) === '1';
            } catch (e) {
                this.closed = false;
            }
        },

        close() {
            this.closed = true;
            try {
                window.localStorage.setItem(this.storageKey, '1');
            } catch (e) {
                // a viewer who cannot store it sees the bar again next time, which is
                // better than failing to close it now
            }
        }
    }));

    // The quick access rail. Only the back-to-top control has state: it stays hidden until
    // the page has been scrolled far enough that jumping back is worth offering.
    Alpine.data('quickAccess', () => ({
        scrolled: false,

        init() {
            this.onScroll();
            window.addEventListener('scroll', () => this.onScroll(), { passive: true });
        },

        onScroll() {
            this.scrolled = window.scrollY > 400;
        },

        toTop() {
            const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
        }
    }));
})

// Start on DOMContentLoaded, not on window "load": app.js and all component scripts are
// deferred (no async), so they have all run - and registered their Alpine.data() in
// "alpine:init" - before DOMContentLoaded fires. Waiting for "load" kept menus, sliders and the
// cookie banner dead until every image and video had downloaded.
// "load" is a safety net in case app.js itself runs after DOMContentLoaded (injected late).
let alpineStarted = false;
const startAlpine = () => {
    if (alpineStarted) {
        return;
    }
    alpineStarted = true;
    Alpine.start();
};
if (document.readyState === 'complete') {
    startAlpine();
} else {
    document.addEventListener('DOMContentLoaded', startAlpine, { once: true });
    window.addEventListener('load', startAlpine, { once: true });
}
